import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faEnvelope, faUserTag, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks';
import { Role } from 'src/interfaces/ManageUsersAndRole.interface';
import useAppToast from 'src/hooks/useAppToast'
import Loader from 'src/utils/Loader';
import { editManagerAsync, getEditUserStaff, getRoleAsync, getRoleStaffList, getUserByIdAsync, updateUserStaff } from 'src/redux/actions/manageUserActions';
import CustomerDropdown from './CustomerDropdown';
import { loggedInAdminAccessToken } from 'src/utils/constants';
import { usePermission } from 'src/hooks/usePermission';
import Select from 'react-select';

const EditUserModal = ({ show, handleClose, user, fetchUserData }) => {
  const dispatch = useAppDispatch();
  const {isRead, isWrite, isDelete} = usePermission();
  const isLoginAdminAcess = !(localStorage.getItem(loggedInAdminAccessToken));
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)
  const isSuperAdmin = fetchedUserAccountData?.user_type === "SUPERADMIN" || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const isSuperuser = fetchedUserAccountData?.is_superuser || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const { successToast, errorToast, warningToast } = useAppToast();
    const emptyUserData = {
  id: null,
  name: '',
  email: '',
  role_id: '',
  is_notify: false,
  status: false,
}
  const [roleList, setRoleList] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [isAllSelect, setIsAllSelect] = useState<boolean>(false);
  const [userData, setUserData] = useState(emptyUserData);

  const userType = fetchedUserAccountData?.user_type || '';

   const resetForm = () => {
  setUserData(emptyUserData);
  setSelectedCustomers([]);
  setIsAllSelect(false);
};

  const onCloseModal = () => {
  resetForm();
  handleClose();
};

  const fetchRoleList = () => {
    setLoading(true);
    if (isSuperuser) {
      dispatch(getRoleAsync())
        .unwrap()
        .then((response) => {
          setRoleList(response?.data ?? []);
        })
        .catch((error) => {
          console.error("error Role List", error);
          errorToast(error?.data?.message || "Failed to load roles.");
        })
        .finally(() => setLoading(false));
    } else {
      dispatch(
        getRoleStaffList({
          paginationdata: { page_num: 1, page_limit: 10, is_available: true },
        })
      ).then((response) => {
        setRoleList(response?.payload?.data ?? []);
      })
        .catch((error) => {
          console.error("error Role List", error);
          errorToast(error?.data?.message || "Failed to load roles.");
        })
        .finally(() => setLoading(false));
    }
  };

  const fetchUserById = async (id: number) => {
    if (isSuperuser) {
      try {
        setLoading(true);
        const response = await dispatch(getUserByIdAsync({ id }));
        const data = response.payload?.data;

        if (data) {
          setUserData({
            id: data.id,
            name: data.name || '',
            email: data.email || '',
            role_id: data.role?.id?.toString() || '',
            is_notify: Boolean(data.is_notify),
            status: Boolean(data.is_active),
          });

          setIsAllSelect(data?.isAccessAllCustomer)

          const customers = data.customer_ids
            ? data.customer_ids.split(',').map((id) => Number(id))
            : [];
          setSelectedCustomers(customers);
        }
      } catch {
        errorToast('Failed to fetch user details.');
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        const response = await dispatch(getEditUserStaff({ id }));
        const data = response.payload?.data;

        if (data) {
          setUserData({
            id: data.id,
            name: data.name || '',
            email: data.email || '',
            role_id: data.role?.id?.toString() || '',
            is_notify: Boolean(data.is_notify),
            status: Boolean(data.is_active),
          });

          setIsAllSelect(data?.isAccessAllCustomer)

          const customers = data.customer_ids
            ? data.customer_ids.split(',').map((id) => Number(id))
            : [];
          setSelectedCustomers(customers);
        }
      } catch {
        errorToast('Failed to fetch user details.');
      } finally {
        setLoading(false);
      }
    }
  };

useEffect(() => {
  if (!show) {
    resetForm();
    return;
  }
  fetchRoleList();
  if (user?.id) {
    fetchUserById(user.id); // edit mode
  } else {
    resetForm(); // create mode
  }
}, [show, user?.id]);

  const handleChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelect = (customerId: number) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSubmit = async () => {
    const { id, name, email, role_id, is_notify, status } = userData;
    const customers = isAllSelect || selectedCustomers.length !== 0;

    const roleName = roleList.find((r) => r.id === Number(role_id))?.name || '';

    if (isSuperuser) {
      if (!id || !name || !email || !role_id || (isLoginAdminAcess && !customers)) {
        warningToast('Please fill all required fields.');
        return;
      }
      const payload = {
        id: Number(id),
        name: name.trim(),
        email: email.trim(),
        role: roleName,
        role_id: Number(role_id),
        is_active: status,
        is_notify,
        is_access_all_customer: isAllSelect
      } as any;

      payload.customer_ids = isAllSelect ? null : selectedCustomers.join(',');
      try {
        setLoading(true);
        const response = await dispatch(editManagerAsync(payload));
        if (response?.payload?.status_code === 200) {
          successToast(response.payload.massage || 'User updated successfully');
          fetchUserData();
          onCloseModal();
        } else {
          warningToast(response?.payload?.massage || 'Failed to update user');
        }
      } catch (err) {
        console.log("catch error", err)
        errorToast('Failed to edit user.');
      } finally {
        setLoading(false);
      }
    } else {
      if (!id || !name || !email || !role_id) {
        warningToast('Please fill all required fields.');
        return;
      }
      const payload = {
        email: email.trim(),
        name: name.trim(),
        user_type: userType,
        role: roleName,
        role_id: Number(role_id),
        is_active: status,
        is_notify
      }
      try {
        setLoading(true);
        const response = await dispatch(updateUserStaff({ id: user?.id, body: payload }));
        if (response?.payload?.status_code === 200) {
          successToast(response.payload.massage || 'User updated successfully');
          fetchUserData();
          onCloseModal();
        } else {
          warningToast(response.payload?.data?.massage || 'Failed to update user');
        }
      } catch (err) {
        console.log("catch error", err)
        errorToast('Failed to edit user.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={onCloseModal} centered size={!isSuperuser ? "sm" : "lg"}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUserPlus} className='me-2 text-success' />
          Edit User
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ position: 'relative' }}>
        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0.3rem',
            }}
          >
            <Loader variant="section" />
          </div>
        )}

        <style>
          {`
            .status-toggle {
              border: 1px solid #dfe3eb;
              border-radius: 0.875rem;
              padding: 0.85rem 1.1rem;
              background-color: #f8fafc;
            }

            .status-toggle__label {
              font-weight: 600;
              color: #475467;
              display: flex;
              align-items: center;
              gap: 0.35rem;
              margin-bottom: 0.35rem;
            }

            .status-toggle__state {
              font-weight: 600;
              font-size: 0.95rem;
            }

            .status-toggle__state--active {
              color: #12b76a;
            }

            .status-toggle__state--inactive {
              color: #667085;
            }

            .status-toggle__switch.form-check {
              margin: 0;
            }

            .status-toggle__switch .form-check-input {
              width: 3.25rem;
              height: 1.75rem;
              cursor: pointer;
              border: none;
            }

            .status-toggle__switch .form-check-input:checked {
              background-color: #0d6efd;
              box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
            }

            .status-toggle__switch .form-check-input:not(:checked) {
              background-color: #d0d5dd;
            }

            .status-toggle__switch .form-check-label {
              display: none;
            }
          `}
        </style>
        <Form>
          <Row className='gy-4'>
            <Col md={!isSuperuser ? 12 : 6}>
              <div className='d-flex flex-column gap-3 h-100'>
                <div>
                  <Form.Label className='required'>
                    <FontAwesomeIcon icon={faUserPlus} className='me-2 text-primary' />
                    User Name
                  </Form.Label>
                  <Form.Control
                    type='text'
                    placeholder="Enter user's full name"
                    value={userData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    disabled={loading || isSuperuser === false}
                    required
                  />
                </div>

                <div>
                  <Form.Label className='required'>
                    <FontAwesomeIcon icon={faEnvelope} className='me-2 text-danger' />
                    Email
                  </Form.Label>
                  <Form.Control
                    type='email'
                    placeholder='user@example.com'
                    value={userData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    disabled={loading || isSuperuser === false}
                  />
                </div>

                <div>
                  <Form.Label className='required'>
                    <FontAwesomeIcon icon={faUserTag} className='me-2 text-warning' />
                    Role
                  </Form.Label>
                  <Select
                    options={roleList.map(role => ({ value: role.id, label: role.name }))}
                    value={
                      roleList.find(r => r.id === Number(userData.role_id))
                        ? { value: Number(userData.role_id), label: roleList.find(r => r.id === Number(userData.role_id))?.name }
                        : null
                    }
                    onChange={(selected) => handleChange('role_id', selected?.value.toString() || '')}
                    isSearchable
                    placeholder={loading ? 'Loading roles...' : 'Select role'}
                    isDisabled={loading}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: '0.375rem',
                        borderColor: '#dfe3eb',
                        minHeight: '38px',
                        boxShadow: 'none',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#212529',
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#6c757d',
                      }),
                    }}
                  />
                </div>

                <div className='status-toggle'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <div>
                      <div className='status-toggle__label'>
                        <FontAwesomeIcon icon={faUsers} className='text-info me-2' />
                        Status
                      </div>
                      <div
                        className={`status-toggle__state ${userData.status
                          ? 'status-toggle__state--active'
                          : 'status-toggle__state--inactive'
                          }`}
                      >
                        {userData.status ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                    <Form.Check
                      type='switch'
                      id='statusSwitch'
                      checked={userData.status}
                      onChange={() => handleChange('status', !userData.status)}
                      label=''
                      className='status-toggle__switch'
                      aria-label='Toggle user active status'
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </Col>
            {isSuperuser && <Col md={6}>
              <div className='d-flex flex-column h-100'>
                <Form.Label className='required'>
                  <FontAwesomeIcon icon={faUsers} className='me-2 text-info' />
                  Customers
                </Form.Label>
                <div className='flex-grow-1 border rounded p-3 bg-light d-flex flex-column'>
                  <CustomerDropdown
                    customerView
                    isAllSelect={isAllSelect}
                    onAllSelect={setIsAllSelect}
                    selectedCustomers={selectedCustomers}
                    onSelect={handleSelect}
                  />
                </div>
              </div>
            </Col>}
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3'>
        {isSuperuser && <Form.Check
          type='checkbox'
          label='Receive email notifications for customer events'
          checked={userData.is_notify}
          onChange={() => handleChange('is_notify', !userData.is_notify)}
          disabled={loading}
        />}
        <div className='ms-md-auto d-flex gap-2'>
          <Button variant='outline-secondary' onClick={onCloseModal} disabled={loading}>
            Cancel
          </Button>
          {isWrite && <Button variant='success' onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' /> Saving...
              </>
            ) : (
              'Edit User'
            )}
          </Button>}
        </div>
      </Modal.Footer>
    </Modal>
  )
};

export default EditUserModal;
